import { FC, useEffect, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { GridColDef } from "@mui/x-data-grid-pro"
import { format, isValid, parseISO } from "date-fns"
import { DataGrid, GoBack } from "@/core/components"
import { FiltersOf, useTitle, useUrlFilters } from "@/core/hooks"
import { AdvertisingContent } from "../interfaces"
import {
  CONTENTS_GROUP_CAP,
  useGetContentsFiltersQuery,
  useGetContentsQuery,
} from "../store"
import {
  CONTENTS_PAGE_SIZE_DEFAULT,
  CONTENTS_PAGE_SIZE_OPTIONS,
  CONTENTS_PAGE_URL_SCHEMA,
} from "../constants"
import {
  bindingsSummaryText,
  buttonsCount,
  filesSummaryText,
} from "../utils/contentsFormatting"
import { formatContentDateTime } from "../utils/formatContentDateTime"
import { resolveContentFilterOptions } from "../utils/resolveContentFilterOptions"
import { useAdvWriteAccess } from "../hooks/useAdvWriteAccess"
import { ContentViewerDialog } from "../components/contents/contentViewerDialog"
import { CreateContentDialog } from "../components/contents/createContentDialog"
import { DeleteContentDialog } from "../components/contents/deleteContentDialog"

interface IProps {}

type PageFilters = FiltersOf<typeof CONTENTS_PAGE_URL_SCHEMA>

const TEXT_CLAMP_SX = {
  whiteSpace: "pre-wrap",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
} as const

export const ContentsPage: FC<IProps> = () => {
  useTitle(`PR ADV - Материалы`)
  const canWrite = useAdvWriteAccess()
  const { read, write } = useUrlFilters(CONTENTS_PAGE_URL_SCHEMA)
  const filters = read()
  const page = filters.page ?? 0
  const pageSize = Math.min(filters.pageSize ?? CONTENTS_PAGE_SIZE_DEFAULT, 100)
  const hasActiveFilters = Boolean(
    filters.campaigns?.length ||
      filters.orders?.length ||
      filters.agents?.length ||
      filters.orderDateFrom ||
      filters.orderDateTo,
  )

  const [dateFrom, setDateFrom] = useState<Date | null>(() =>
    filters.orderDateFrom ? parseISO(filters.orderDateFrom) : null,
  )
  const [dateTo, setDateTo] = useState<Date | null>(() =>
    filters.orderDateTo ? parseISO(filters.orderDateTo) : null,
  )
  const [dateRangeError, setDateRangeError] = useState<string | null>(null)
  // URL проверяется синхронно: deep link и back/forward не должны успеть
  // отправить запрос до эффекта синхронизации полей календаря.
  const queryDateRangeError =
    filters.orderDateFrom &&
    filters.orderDateTo &&
    filters.orderDateFrom > filters.orderDateTo
      ? "Дата «с» не может быть позже даты «по»"
      : null
  const effectiveDateRangeError = dateRangeError ?? queryDateRangeError
  const [viewerContentId, setViewerContentId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdvertisingContent | null>(
    null,
  )
  const [createOpen, setCreateOpen] = useState(false)

  useEffect(() => {
    setDateFrom(filters.orderDateFrom ? parseISO(filters.orderDateFrom) : null)
    setDateTo(filters.orderDateTo ? parseISO(filters.orderDateTo) : null)
    setDateRangeError(null)
  }, [filters.orderDateFrom, filters.orderDateTo])

  const { data: filterOptions, isLoading: filtersLoading } =
    useGetContentsFiltersQuery({})

  // E5: кап 100 различных ID в группе — клиентская блокировка, запрос не отправляется.
  const groupCapError = (() => {
    const groups: Array<{ label: string; ids?: number[] }> = [
      { label: "campaigns", ids: filters.campaigns },
      { label: "orders", ids: filters.orders },
      { label: "agents", ids: filters.agents },
    ]
    for (const group of groups) {
      if (new Set(group.ids ?? []).size > CONTENTS_GROUP_CAP) {
        return `В группе «${group.label}» не может быть больше 100 ID`
      }
    }
    return null
  })()
  const queryInvalid = groupCapError !== null || queryDateRangeError !== null

  const { data, isFetching, isLoading, isError, refetch } = useGetContentsQuery(
    {
      queryParams: {
        campaigns: filters.campaigns,
        orders: filters.orders,
        agents: filters.agents,
        orderDateFrom: filters.orderDateFrom,
        orderDateTo: filters.orderDateTo,
        // Defense-in-depth: pageSize клампится серверным капом 100 при чтении
        // и используется синхронно для limit и offset.
        limit: pageSize,
        offset: page * pageSize,
      },
    },
    { skip: queryInvalid },
  )

  // ЕДИНСТВЕННОЕ функциональное обновление URL на любое изменение (E3):
  // значения-дефолты не пишутся, сброс страницы — в той же записи.
  const applyUrlState = (next: PageFilters) => {
    write({
      ...next,
      page: next.page ? next.page : undefined,
      pageSize:
        next.pageSize && next.pageSize !== CONTENTS_PAGE_SIZE_DEFAULT
          ? next.pageSize
          : undefined,
    })
  }

  const resetFilters = () => {
    setDateFrom(null)
    setDateTo(null)
    setDateRangeError(null)
    write({})
  }

  const setDateFilter = (
    key: "orderDateFrom" | "orderDateTo",
    value: Date | null,
  ) => {
    if (key === "orderDateFrom") {
      setDateFrom(value)
    } else {
      setDateTo(value)
    }
    const candidate =
      value && isValid(value) ? format(value, "yyyy-MM-dd") : undefined
    const currentFrom =
      key === "orderDateFrom"
        ? candidate
        : dateFrom && isValid(dateFrom)
          ? format(dateFrom, "yyyy-MM-dd")
          : filters.orderDateFrom
    const currentTo =
      key === "orderDateTo"
        ? candidate
        : dateTo && isValid(dateTo)
          ? format(dateTo, "yyyy-MM-dd")
          : filters.orderDateTo

    // FR-7: from > to блокируется до записи URL (строковое сравнение
    // корректно для YYYY-MM-DD).
    // невалидный диапазон — только ошибка поля, URL и запросы не трогаем
    if (currentFrom && currentTo && currentFrom > currentTo) {
      setDateRangeError("Дата «с» не может быть позже даты «по»")
      return
    }
    setDateRangeError(null)
    applyUrlState({
      ...filters,
      orderDateFrom: currentFrom,
      orderDateTo: currentTo,
      page: undefined,
    })
  }

  const onPaginationModelChange = (model: {
    page: number
    pageSize: number
  }) => {
    // Смена pageSize сбрасывает page в 0 В ТОЙ ЖЕ записи (§2.7).
    const pageSizeChanged = model.pageSize !== pageSize
    applyUrlState({
      ...filters,
      page: pageSizeChanged ? undefined : model.page,
      pageSize: model.pageSize,
    })
  }

  const campaignFilter = resolveContentFilterOptions(
    "campaigns",
    filterOptions?.campaigns ?? [],
    filters.campaigns,
  )
  const orderFilter = resolveContentFilterOptions(
    "orders",
    filterOptions?.orders ?? [],
    filters.orders,
  )
  const agentFilter = resolveContentFilterOptions(
    "agents",
    filterOptions?.agents ?? [],
    filters.agents,
  )

  const columns: GridColDef<AdvertisingContent>[] = [
    {
      field: "text",
      headerName: "Текст",
      flex: 2,
      renderCell: ({ row }) => <Box sx={TEXT_CLAMP_SX}>{row.text ?? "—"}</Box>,
    },
    {
      field: "files",
      headerName: "Файлы",
      flex: 2,
      renderCell: ({ row }) => filesSummaryText(row),
    },
    {
      field: "buttons",
      headerName: "Кнопки",
      flex: 1,
      renderCell: ({ row }) => {
        const count = buttonsCount(row)
        return count > 0 ? `Кнопки: ${count}` : "—"
      },
    },
    {
      field: "created_at",
      headerName: "Создан",
      flex: 1,
      renderCell: ({ row }) => formatContentDateTime(row.created_at),
    },
    {
      field: "bindings",
      headerName: "Привязки",
      flex: 1,
      minWidth: 240,
      renderCell: ({ row }) => {
        const labels = [
          ...row.order_ids.map((id) => `Ордер #${id}`),
          ...row.campaign_ids.map((id) => `Кампания #${id}`),
        ]
        if (labels.length === 0) {
          return <Chip size="small" label="Нет привязок" />
        }
        return (
          <Stack spacing={0.5} sx={{ py: 0.5, width: "100%" }}>
            <Typography variant="body2">{bindingsSummaryText(row)}</Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {labels.slice(0, 3).map((label) => (
                <Chip key={label} size="small" label={label} />
              ))}
              {labels.length > 3 && (
                <Tooltip title={labels.join(" · ")} describeChild>
                  <Chip
                    size="small"
                    label={`+${labels.length - 3}`}
                    tabIndex={0}
                  />
                </Tooltip>
              )}
            </Stack>
          </Stack>
        )
      },
    },
    {
      field: "actions",
      headerName: "Действия",
      flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setViewerContentId(row.id)}>
            Просмотр
          </Button>
          {canWrite && (
            <Button
              size="small"
              color="error"
              onClick={() => setDeleteTarget(row)}
            >
              Удалить
            </Button>
          )}
        </Stack>
      ),
    },
  ]

  const showGrid =
    !queryInvalid &&
    !filtersLoading &&
    !isLoading &&
    !isError &&
    (data?.count ?? 0) > 0

  return (
    <Container maxWidth={false}>
      <GoBack />
      <Box mb={1} sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">Материалы</Typography>
        {canWrite && (
          <Button onClick={() => setCreateOpen(true)}>Создать материал</Button>
        )}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
        <Autocomplete
          multiple
          sx={{ minWidth: 240 }}
          options={campaignFilter.options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={campaignFilter.value}
          loading={filtersLoading}
          onChange={(_, values) =>
            applyUrlState({
              ...filters,
              campaigns: values.map((v) => v.id),
              page: undefined,
            })
          }
          renderInput={(params) => (
            <TextField {...params} label="Кампании" variant="outlined" />
          )}
        />
        <Autocomplete
          multiple
          sx={{ minWidth: 240 }}
          disabled={orderFilter.options.length === 0}
          options={orderFilter.options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={orderFilter.value}
          loading={filtersLoading}
          onChange={(_, values) =>
            applyUrlState({
              ...filters,
              orders: values.map((v) => v.id),
              page: undefined,
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Ордера"
              variant="outlined"
              helperText={
                orderFilter.options.length === 0
                  ? "Пока нет материалов"
                  : undefined
              }
            />
          )}
        />
        <Autocomplete
          multiple
          sx={{ minWidth: 240 }}
          options={agentFilter.options}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={agentFilter.value}
          loading={filtersLoading}
          onChange={(_, values) =>
            applyUrlState({
              ...filters,
              agents: values.map((v) => v.id),
              page: undefined,
            })
          }
          renderInput={(params) => (
            <TextField {...params} label="Агенты" variant="outlined" />
          )}
        />
        <DatePicker
          label="Дата начала ордера (с)"
          format="dd.MM.yyyy"
          value={dateFrom}
          onChange={(value) => setDateFilter("orderDateFrom", value)}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          label="Дата начала ордера (по)"
          format="dd.MM.yyyy"
          value={dateTo}
          onChange={(value) => setDateFilter("orderDateTo", value)}
          slotProps={{
            textField: {
              size: "small",
              error: !!effectiveDateRangeError,
              helperText: effectiveDateRangeError ?? undefined,
            },
          }}
        />
      </Stack>
      {(!!filters.agents?.length ||
        !!filters.orderDateFrom ||
        !!filters.orderDateTo) && (
        <Typography variant="caption" color="text.secondary" component="p">
          Агент и обе границы дат должны подходить одному ордеру:
          непосредственно привязанному к материалу либо принадлежащему
          привязанной кампании
        </Typography>
      )}
      {!!groupCapError && <Alert severity="error">{groupCapError}</Alert>}
      {!queryInvalid && isLoading && <LinearProgress sx={{ my: 1 }} />}
      {!queryInvalid && isError && !isFetching && (
        <Box my={2}>
          <Typography paragraph>Не удалось загрузить материалы</Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Повторить
          </Button>
        </Box>
      )}
      {!queryInvalid && !isError && !isLoading && data?.count === 0 && (
        <Box my={2}>
          {hasActiveFilters ? (
            <>
              <Typography paragraph>
                По заданным фильтрам ничего не найдено
              </Typography>
              <Button variant="outlined" onClick={resetFilters}>
                Сбросить фильтры
              </Button>
            </>
          ) : (
            <>
              <Typography paragraph>Пока нет материалов</Typography>
              {canWrite && (
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                  Создать материал
                </Button>
              )}
            </>
          )}
        </Box>
      )}
      {showGrid && (
        <Box height="85vh">
          <DataGrid<AdvertisingContent>
            columns={columns}
            rows={data?.items ?? []}
            getRowHeight={() => "auto"}
            rowCount={data?.count ?? 0}
            loading={isFetching}
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={CONTENTS_PAGE_SIZE_OPTIONS}
            initialState={{
              sorting: { sortModel: [{ field: "id", sort: "desc" }] },
            }}
          />
        </Box>
      )}
      {viewerContentId !== null && (
        <ContentViewerDialog
          contentId={viewerContentId}
          open
          onClose={() => setViewerContentId(null)}
        />
      )}
      {createOpen && (
        <CreateContentDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {deleteTarget !== null && (
        <DeleteContentDialog
          open
          content={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </Container>
  )
}
